import { HashLocationStrategy, LocationStrategy, PathLocationStrategy } from '@angular/common';
import { HTTP_INTERCEPTORS, HttpClient, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { APP_INITIALIZER, InjectionToken, NgModule } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { MSAL_GUARD_CONFIG, MSAL_INSTANCE, MSAL_INTERCEPTOR_CONFIG, MsalBroadcastService, MsalGuard, MsalGuardConfiguration, MsalModule, MsalService } from '@azure/msal-angular';
import { BrowserCacheLocation, InteractionType, IPublicClientApplication, LogLevel, PublicClientApplication } from '@azure/msal-browser';
import { LeafletModule } from '@bluehalo/ngx-leaflet';
import { BreadcrumbModule, DropdownModule, FooterModule, HeaderModule, ProgressModule, SidebarModule } from '@coreui/angular';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { config } from '@fortawesome/fontawesome-svg-core';
import { TranslateLoader, TranslateModule } from '@ngx-translate/core'; // Ref: https://github.com/ngx-translate/core
import { TranslateHttpLoader } from '@ngx-translate/http-loader';
import { ChartsModule, SparklineModule } from '@progress/kendo-angular-charts';
import { DateInputsModule } from '@progress/kendo-angular-dateinputs';
import { DialogModule, WindowContainerService, WindowModule } from '@progress/kendo-angular-dialog';
import { EditorModule } from '@progress/kendo-angular-editor';
import { ExcelModule, GridModule, PDFModule } from '@progress/kendo-angular-grid';
import { InputsModule } from '@progress/kendo-angular-inputs';
import '@progress/kendo-angular-intl/locales/en/all';
import '@progress/kendo-angular-intl/locales/es/all';
import '@progress/kendo-angular-intl/locales/fr/all';
import '@progress/kendo-angular-intl/locales/it/all';
import { MessageService } from '@progress/kendo-angular-l10n';
import { LabelModule } from "@progress/kendo-angular-label";
import { TooltipDirective, TooltipModule, TooltipsModule } from "@progress/kendo-angular-tooltip";
import { LaddaModule } from 'angular2-ladda';
import { AlertModule } from 'ngx-bootstrap/alert';
import { BsDropdownModule } from 'ngx-bootstrap/dropdown';
import { TabsModule } from 'ngx-bootstrap/tabs';
import { NgcCookieConsentConfig, NgcCookieConsentModule } from 'ngx-cookieconsent'; // https://github.com/tinesoft/ngx-cookieconsent
import { LocalStorageService } from 'ngx-localstorage';
import { NgProgressbar } from 'ngx-progressbar';
import { NgxSpinnerModule } from 'ngx-spinner'; // Ref: https://github.com/Napster2210/ngx-spinner
import { ToastrModule } from 'ngx-toastr'; // Ref: https://github.com/scttcper/ngx-toastr
import { environment } from '../environments/environment';
import { AppComponent } from './app.component';
import { appCommonConstants } from './app.constants';
import { AppRouting } from './app.routing';
import { AppHttpDateInterceptor } from './interceptors/http.date.interceptor';
import { AppHttpInterceptor } from './interceptors/http.interceptor';
import { MSALInterceptorConfigFactory, MsalSkipInterceptor } from './interceptors/http.msalSkipInterceptor.interceptor';
import { AppCrmInitService } from './modules/crm/services/crm-init';
import { AppPlanningInitService } from './modules/planning/services/planning-init';
import { AppSessionManagerInitService } from './modules/session-manager/services/session-manager-init';
import { AppCurrencyProvider } from './providers/currency.provider';
import { AppLocaleProvider } from './providers/locale.provider';
import { AppConfigService } from './services/configuration.service';
import { AppKendoTranslateService } from './services/kendoTranslate.service';
import { AppLocaleService } from './services/locale.service';
import { AppLoggerService } from './services/logger.service';
import { AppSignalRService } from './services/signalR.service';

export const DEFAULT_CURRENCY_CODE = new InjectionToken<string>('DEFAULT_CURRENCY_CODE');  // Ref: https://angular.io/api/core/DEFAULT_CURRENCY_CODE

const COOKIE_CONFIG: NgcCookieConsentConfig = {
  cookie: {
    domain: location.hostname, // 'localhost' or 'your.domain.com' // it is mandatory to set a domain, for cookies to work properly (see https://goo.gl/S2Hy2A)
    expiryDays: 90
  },
  dismissOnScroll: true,
  position: "top",
  palette: {
    popup: {
      background: '#000'
    },
    button: {
      background: '#f1d600'
    }
  },
  theme: 'edgeless',
  type: "info"
};

const isIE = window.navigator.userAgent.indexOf("MSIE ") > -1 || window.navigator.userAgent.indexOf("Trident/") > -1;

export function loggerCallback(logLevel: LogLevel, message: string) {
  console.log(message);
}

export function MSALInstanceFactory(): IPublicClientApplication {
  return new PublicClientApplication({
    auth: {
      authority: environment.azureLogin?.b2cPolicies.authorities.signUpSignIn,
      clientId: environment.azureLogin?.clientId ?? "",
      knownAuthorities: [environment.azureLogin?.b2cPolicies.authorityDomain ?? ""],
      navigateToLoginRequestUrl: false,
      postLogoutRedirectUri: environment.azureLogin?.postLogoutRedirectUri,
      redirectUri: environment.azureLogin?.redirectUri
    },
    cache: {
      cacheLocation: BrowserCacheLocation.LocalStorage,
      storeAuthStateInCookie: isIE, // set to true for IE 11
    },
    system: {
      allowRedirectInIframe: environment.azureLogin.interactionType === "popup" ? false : true,
      loggerOptions: {
        loggerCallback,
        logLevel: LogLevel.Info,
        piiLoggingEnabled: false
      }
    }
  });
}

export function MSALGuardConfigFactory(): MsalGuardConfiguration {
  return { interactionType: environment.azureLogin?.interactionType ?? InteractionType.Redirect };
}

// AoT requires an exported function for factories
export function HttpLoaderFactory(http: HttpClient) {
  return new TranslateHttpLoader(http, './assets/i18n/', '.json');
}

@NgModule({
  bootstrap: [AppComponent],
  declarations: [
    AppComponent
  ],
  exports: [],
  imports: [
    AlertModule.forRoot(),
    AppRouting,
    BreadcrumbModule,
    BrowserAnimationsModule,  // Required animations module
    BsDropdownModule.forRoot(),
    ChartsModule,
    DateInputsModule,
    DialogModule,
    DropdownModule,
    EditorModule,
    ExcelModule,
    FontAwesomeModule,
    FooterModule,
    FormsModule,
    GridModule,
    HeaderModule,
    InputsModule,
    LabelModule,
    LaddaModule.forRoot({
      style: "zoom-in",
      //spinnerSize: 40,
      spinnerColor: "#444",
      spinnerLines: 12
    }),
    LeafletModule,
    MsalModule,
    NgcCookieConsentModule.forRoot(COOKIE_CONFIG),
    NgProgressbar,
    NgxSpinnerModule,
    PDFModule,
    ProgressModule,
    ReactiveFormsModule,
    SidebarModule,
    SparklineModule,
    TabsModule,
    TabsModule.forRoot(),
    ToastrModule.forRoot(),   // ToastrModule added
    TooltipDirective,
    TooltipModule,
    TooltipsModule,
    TranslateModule.forRoot({
      loader: {
        provide: TranslateLoader,
        useFactory: HttpLoaderFactory,
        deps: [HttpClient]
      }
    }),
    WindowModule,
  ],
  providers: [
    // <!-- Msal: autenticazione tramite Azure, mantenere quest'ordine di definizione dei providers (prima Msal)
    {
      provide: HTTP_INTERCEPTORS,
      useClass: MsalSkipInterceptor,  //MsalInterceptor,
      multi: true
    },
    {
      provide: MSAL_INSTANCE,
      useFactory: MSALInstanceFactory
    },
    {
      provide: MSAL_GUARD_CONFIG,
      useFactory: MSALGuardConfigFactory
    },
    {
      provide: MSAL_INTERCEPTOR_CONFIG,
      useFactory: MSALInterceptorConfigFactory
    },
    MsalService,
    MsalGuard,
    MsalBroadcastService,
    // Msal -->
    [
      provideHttpClient(),
      {
        provide: APP_INITIALIZER,
        useFactory: (configService: AppConfigService) => {
          return () => configService.loadConfig();
        },
        deps: [AppConfigService],
        multi: true
      }
    ],
    [AppCurrencyProvider],
    [AppLocaleProvider],
    { provide: MessageService, useClass: AppKendoTranslateService },  // https://www.telerik.com/kendo-angular-ui/components/globalization/localization/messages/#toc-using-the-message-service
    {
      provide: LocationStrategy,  // Configurazione della strategia di routing (HashLocationStrategy (.../login) o PathLocationStrategy (es. .../#/login) )
      useClass: environment.pathLocationStrategy === "path" ? PathLocationStrategy : HashLocationStrategy
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: AppHttpInterceptor,
      multi: true
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: AppHttpDateInterceptor,
      multi: true
    },
    provideHttpClient(withInterceptorsFromDi()),
    WindowContainerService,
  ],
})

export class AppModule {
  constructor(
    private crmInitService: AppCrmInitService,
    private locale: AppLocaleService,
    private localStorage: LocalStorageService,
    private logger: AppLoggerService,
    private planningInitService: AppPlanningInitService,
    private sessionManagerInitService: AppSessionManagerInitService,
    private signalrService: AppSignalRService) {
    config.autoA11y = true

    /** Definizione del metodo che ritorna la data di pianificazione di default */
    Date.prototype.getDefaultPlanningDate = function () {
      let data = localStorage.get(appCommonConstants.global.defaultPlanningDate);
      return data ? new Date(data as Date) : new Date(new Date().getPlainDate?.() ?? new Date());
    }

    // Inizializzazione locale
    locale.init();
    // Inizializzazione SignalR
    signalrService.initialize();

    logger.console.info(`App running, appVersion: ${environment.appSettings.appVersion}`, "");

    // Inizializzazione moduli (pro SignalR)
    crmInitService.init();
    planningInitService.init();
    sessionManagerInitService.init();
  }
}
